<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import PipelineCard from './PipelineCard.vue';

const store = useStore();
const { t } = useI18n();

const byOutcome = computed(
  () => store.getters['pipeline/getConversationsByOutcome']
);
const won = computed(() => byOutcome.value.won || []);
const lost = computed(() => byOutcome.value.lost || []);

// Conversion rate over the recent-closed window the API already returns
// (won + lost, capped at ~50 by the backend). A general "are we converting?"
// signal — the precise/trend numbers live in Pipeline Analytics.
const total = computed(() => won.value.length + lost.value.length);
const conversionRate = computed(() =>
  total.value > 0 ? Math.round((won.value.length / total.value) * 100) : 0
);
</script>

<template>
  <div class="flex flex-col h-full w-full max-w-[66rem] mx-auto p-6 gap-3">
    <!-- Conversion rate — single-line, discreet meter (does not push cards down) -->
    <div v-if="total > 0" class="flex items-center gap-3">
      <span
        class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap"
      >
        {{ t('PIPELINE.CONVERSION.LABEL', { count: total }) }}
      </span>
      <div
        class="flex-1 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden"
      >
        <div
          class="h-full bg-woot-500 rounded-full"
          :style="{ width: `${conversionRate}%` }"
        />
      </div>
      <span
        class="text-xs font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap"
      >
        {{ t('PIPELINE.CONVERSION.RATE', { rate: conversionRate }) }}
      </span>
    </div>

    <div class="grid gap-4 flex-1 min-h-0 w-full grid-cols-1 md:grid-cols-2">
      <div class="flex flex-col min-h-0">
        <div
          class="flex flex-col h-full min-h-0 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
        >
          <div
            class="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center gap-2"
          >
            <h3 class="font-semibold text-slate-900 dark:text-slate-25">
              {{ t('PIPELINE.OUTCOME.WON') }}
            </h3>
            <span class="text-xs text-slate-500">
              {{
                t('PIPELINE.CONVERSION.COLUMN_COUNT', {
                  count: won.length,
                  total,
                })
              }}
            </span>
          </div>
          <div class="flex-1 p-2 overflow-y-auto space-y-2 min-h-[200px]">
            <PipelineCard
              v-for="conversation in won"
              :key="conversation.id"
              :conversation="conversation"
            />
            <p
              v-if="won.length === 0"
              class="text-sm text-slate-500 dark:text-slate-400 text-center mt-8"
            >
              {{ t('PIPELINE.EMPTY_STATE.MESSAGE') }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col min-h-0">
        <div
          class="flex flex-col h-full min-h-0 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
        >
          <div
            class="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center gap-2"
          >
            <h3 class="font-semibold text-slate-900 dark:text-slate-25">
              {{ t('PIPELINE.OUTCOME.LOST') }}
            </h3>
            <span class="text-xs text-slate-500">
              {{
                t('PIPELINE.CONVERSION.COLUMN_COUNT', {
                  count: lost.length,
                  total,
                })
              }}
            </span>
          </div>
          <div class="flex-1 p-2 overflow-y-auto space-y-2 min-h-[200px]">
            <PipelineCard
              v-for="conversation in lost"
              :key="conversation.id"
              :conversation="conversation"
            />
            <p
              v-if="lost.length === 0"
              class="text-sm text-slate-500 dark:text-slate-400 text-center mt-8"
            >
              {{ t('PIPELINE.EMPTY_STATE.MESSAGE') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
